import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { EquipmentGroupStId, SapKey, EquipmentCategory, Selections } from 'app/types/types'

export interface SubServiceError {
  groupId: EquipmentGroupStId
  category: EquipmentCategory
  serviceId: string
  subServiceKey: SapKey
  groupName: string
  hasValidAnswer: boolean
}

export interface ServiceConfigurationValidationState {
  // Track selections by groupId -> category -> scope -> selections
  selectionsByGroup: Record<EquipmentGroupStId, Record<EquipmentCategory, Record<string, Selections>>>
  // Track errors for each subservice
  errors: SubServiceError[]
  // Track if validation has been run
  isValidated: boolean
}

const initialState: ServiceConfigurationValidationState = {
  selectionsByGroup: {},
  errors: [],
  isValidated: false
}

const serviceConfigurationSlice = createSlice({
  name: 'serviceConfiguration',
  initialState,
  reducers: {
    updateSubServiceSelection: (
      state,
      action: PayloadAction<{
        groupId: EquipmentGroupStId
        category: EquipmentCategory
        scope: string
        subServiceKey: SapKey
        value: string | string[] | null
      }>
    ) => {
      const { groupId, category, scope, subServiceKey, value } = action.payload

      if (!state.selectionsByGroup[groupId]) {
        state.selectionsByGroup[groupId] = {} as Record<EquipmentCategory, Record<string, Selections>>
      }
      if (!state.selectionsByGroup[groupId][category]) {
        state.selectionsByGroup[groupId][category] = {}
      }
      if (!state.selectionsByGroup[groupId][category][scope]) {
        state.selectionsByGroup[groupId][category][scope] = {}
      }

      state.selectionsByGroup[groupId][category][scope][subServiceKey] = value
      state.isValidated = false
    },

    setErrors: (state, action: PayloadAction<SubServiceError[]>) => {
      state.errors = action.payload
      state.isValidated = true
    },

    clearErrors: state => {
      state.errors = []
      state.isValidated = false
    },

    resetServiceConfiguration: state => {
      state.selectionsByGroup = {}
      state.errors = []
      state.isValidated = false
    },

    // Initialize selections for a group from offer.equipmentGroups data
    initializeGroupSelections: (
      state,
      action: PayloadAction<{
        groupId: EquipmentGroupStId
        category: EquipmentCategory
        scope: string
        selections: Selections
      }>
    ) => {
      const { groupId, category, scope, selections } = action.payload

      if (!state.selectionsByGroup[groupId]) {
        state.selectionsByGroup[groupId] = {} as Record<EquipmentCategory, Record<string, Selections>>
      }
      if (!state.selectionsByGroup[groupId][category]) {
        state.selectionsByGroup[groupId][category] = {}
      }

      // Only initialize if not already set (don't override user changes)
      if (!state.selectionsByGroup[groupId][category][scope]) {
        state.selectionsByGroup[groupId][category][scope] = selections
      }
    }
  }
})

export const {
  updateSubServiceSelection,
  setErrors,
  clearErrors,
  resetServiceConfiguration,
  initializeGroupSelections
} = serviceConfigurationSlice.actions

export default serviceConfigurationSlice.reducer
